import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Code2, CodeXml, FileJson, ArrowRight, Loader2, SquareTerminal, Brain, Server, Shield } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { unifiedFunctionsApi } from '@/services/unifiedFunctionsApi';

interface Stats {
  functions: number;
  tools: number;
  evalFunctions: number;
}

export function DeveloperPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<Stats>({ functions: 0, tools: 0, evalFunctions: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const functions = await unifiedFunctionsApi.list();
        setStats({
          functions: functions.length,
          tools: functions.filter(f => f.is_tool).length,
          evalFunctions: functions.filter(f => f.is_eval_function).length,
        });
      } catch (err) {
        console.error('Failed to fetch stats:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  const cards = [
    {
      title: 'IDE',
      description: 'Unified development environment: edit, test, and iterate on agents in one place',
      icon: SquareTerminal,
      path: '/developer/ide',
      stats: null,
      featured: true,
    },
    {
      title: 'Functions',
      description: 'Create and manage unified functions that can serve as agent tools or eval functions',
      icon: Code2,
      path: '/developer/functions',
      stats: loading ? null : (
        <div className="flex gap-2 mt-2">
          <Badge variant="secondary">{stats.functions} total</Badge>
          <Badge variant="outline" className="text-blue-600 border-blue-600">{stats.tools} tools</Badge>
          <Badge variant="outline" className="text-green-600 border-green-600">{stats.evalFunctions} eval</Badge>
        </div>
      ),
    },
    {
      title: 'Schemas',
      description: 'Define and manage JSON schemas for structured outputs and tool parameters',
      icon: FileJson,
      path: '/developer/schemas',
      stats: null,
    },
    {
      title: 'Knowledge',
      description: 'Unified knowledge base: entities, memories, and business context for agents',
      icon: Brain,
      path: '/developer/knowledge',
      stats: null,
    },
    {
      title: 'MCP Servers',
      description: 'Connect external tools and data sources via the Model Context Protocol',
      icon: Server,
      path: '/settings',
      stats: null,
    },
    {
      title: 'SSO & Directory',
      description: 'Configure SAML/OIDC single sign-on and SCIM directory synchronization',
      icon: Shield,
      path: '/developer/sso',
      stats: null,
    },
    {
      title: 'Embed',
      description: 'Create tokens and configure widgets for embedding agents in external apps',
      icon: CodeXml,
      path: '/developer/embed',
      stats: null,
    },
  ];

  return (
    <div className="container max-w-4xl py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Developer</h1>
        <p className="text-muted-foreground">
          Build, test, and manage agents and custom functions
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {cards.map((card) => (
          <Card
            key={card.path}
            className={`cursor-pointer hover:border-primary/50 transition-colors group ${
              card.featured ? 'md:col-span-2 border-primary/30 bg-primary/5' : ''
            }`}
            onClick={() => navigate(card.path)}
          >
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className={`p-2 rounded-lg w-fit ${card.featured ? 'bg-primary/20' : 'bg-primary/10'}`}>
                  <card.icon className={`text-primary ${card.featured ? 'h-7 w-7' : 'h-6 w-6'}`} />
                </div>
                <div className="flex items-center gap-2">
                  {card.featured && <Badge>Recommended</Badge>}
                  <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                </div>
              </div>
              <CardTitle className={card.featured ? 'mt-4 text-xl' : 'mt-4'}>{card.title}</CardTitle>
              <CardDescription>{card.description}</CardDescription>
            </CardHeader>
            <CardContent>
              {loading && card.stats !== null ? (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              ) : (
                card.stats
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

export default DeveloperPage;
